// Unified Review Workflow Integration — Reviews_Due_Queue data-access hook.
//
// This module owns the READ side of the Dashboard Reviews_Due_Queue: the
// org-scoped query key and the query hook that consumes the new (org-scoped)
// backend GET endpoint `GET /api/review-cycles/reviews-due`. The read uses the
// default `getQueryFn` configured on the shared `queryClient`
// (see `@/lib/queryClient`), which issues a GET to `queryKey.join("/")` and
// therefore needs no CSRF token (Req 2.8).
//
// Query-key / URL convention (verified against `@/lib/queryClient`):
//   The repo's default `getQueryFn` builds the request URL by calling
//   `queryKey.join("/")`. The query key is therefore an array of path segments
//   that joins to exactly the GET URL it fetches. The endpoint is implicitly
//   scoped to the authenticated user's session organization server-side, so the
//   hook only ever sees the session org's due cycles (Req 2.8).
//
// The returned `UseQueryResult` exposes the distinct states the
// `ReviewsDueQueue` component renders: `isLoading` (in progress — Req 2.4),
// a successful empty `data` array (explicit empty state — Req 2.5), and
// `isError` with `refetch` (generic error + retry re-issuing the same
// org-scoped request — Req 2.6).
//
// References: design.md "Data-access hooks → useReviewsDue", `@/lib/queryClient`
// (getQueryFn), `@/lib/reviewWorkflow/due` (DueReviewCycle).

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { DueReviewCycle } from "@/lib/reviewWorkflow/due";

// ---------------------------------------------------------------------------
// Query key — an array of path segments joined with "/" to form the GET URL.
// The endpoint is org-scoped server-side from the authenticated session.
// ---------------------------------------------------------------------------

/** `GET /api/review-cycles/reviews-due` — the org-scoped reviews-due queue. */
export const reviewsDueKey = () =>
  ["/api/review-cycles", "reviews-due"] as const;

// ---------------------------------------------------------------------------
// Read hook — default getQueryFn (GET, no CSRF).
// ---------------------------------------------------------------------------

/**
 * Reads the organization-scoped reviews-due queue
 * (`GET /api/review-cycles/reviews-due`): the Review_Cycles for the
 * authenticated user's organization that are due for a unified pass, each
 * carrying its customer name and `dueDate`, ordered by `dueDate` ascending by
 * the server (Req 2.1, 2.2, 2.8).
 *
 * The caller (`ReviewsDueQueue`) derives its presentation from the returned
 * result's distinct states: `isLoading` for the in-progress state (Req 2.4),
 * a successful empty `data` array for the explicit empty state (Req 2.5), and
 * `isError` together with `refetch` for the generic error state and its retry
 * affordance, which re-issues the same org-scoped request (Req 2.6).
 */
export function useReviewsDue(): UseQueryResult<DueReviewCycle[]> {
  return useQuery<DueReviewCycle[]>({
    queryKey: reviewsDueKey(),
  });
}
